// บัญชีที่ติดเพดานจำนวนครั้งเข้าสู่ระบบ พร้อมปุ่มปลดให้ทันที
//
// เกือบทุกครั้งคนที่ติดคือครูที่ลืมรหัสผ่าน ไม่ใช่คนไล่เดารหัส และเดิมแอดมินทำอะไรไม่ได้
// นอกจากบอกให้รอ 15 นาที การ์ดนี้จึงอยู่เหนือประวัติการเข้าระบบ ซึ่งเป็นหน้าที่แอดมิน
// เปิดอยู่แล้วตอนมีคนโทรมาบอกว่าเข้าไม่ได้
//
// ตัวนับอยู่ใน memory ของเซิร์ฟเวอร์ ไม่ใช่ในฐานข้อมูล — รายการจึงว่างหลังรีสตาร์ท
// และการปลดมีผลทันทีโดยไม่ต้องแตะรหัสผ่านหรือสถานะบัญชี
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Table } from "react-bootstrap";
import axiosInstance from "../../services/axiosInterceptor";
import { SOURCE_LABEL } from "./shared";

const API = process.env.REACT_APP_API;

// เวลาที่เพดานจะหมดอายุเอง — บอกเป็นเวลานาฬิกา ไม่ใช่ตัวนับถอยหลัง
// เพราะการ์ดนี้ไม่ได้รีเฟรชตัวเองทุกวินาที ตัวเลขถอยหลังที่ค้างอยู่จะหลอกตามากกว่าช่วย
const resetText = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });
};

export default function LoginLocksCard() {
  const [rows, setRows]     = useState([]);
  const [busy, setBusy]     = useState(false);
  const [note, setNote]     = useState(null);
  const [window_, setWindow] = useState(15);

  const load = useCallback(() => {
    setBusy(true);
    return axiosInstance
      .get(`${API}/admin/login-locks`)
      .then((res) => {
        setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
        if (res.data?.windowMinutes) setWindow(res.data.windowMinutes);
      })
      .catch(() => setRows([]))
      .finally(() => setBusy(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const unlock = (row) => {
    setBusy(true);
    setNote(null);
    axiosInstance
      .post(`${API}/admin/login-locks/unlock`, { source: row.source, username: row.username })
      .then((res) => {
        setNote({ variant: 'success', text: `${row.name || row.username}: ${res.data?.message || 'ปลดล็อกเรียบร้อย'}` });
        return load();
      })
      .catch((err) => {
        setNote({ variant: 'danger', text: err.response?.data?.message || 'ปลดล็อกไม่สำเร็จ' });
      })
      .finally(() => setBusy(false));
  };

  const lockedCount = rows.filter((r) => r.locked).length;

  return (
    <Card className="card-one mb-3">
      <Card.Header className="d-flex flex-wrap align-items-center gap-2">
        <Card.Title as="h6" className="mb-0">
          ถูกกันไม่ให้เข้าสู่ระบบ
          {lockedCount > 0 && <Badge bg="danger" className="ms-2">{lockedCount}</Badge>}
        </Card.Title>
        <span className="text-secondary fs-xs">
          พิมพ์รหัสผิดถี่เกินเพดานจะถูกกันไว้ {window_} นาที — ปลดแล้วเข้าได้ทันที
        </span>
        <Button size="sm" variant="outline-primary" className="ms-auto" onClick={load} disabled={busy}>
          <i className="ri-refresh-line me-1"></i>รีเฟรช
        </Button>
      </Card.Header>
      <Card.Body className={rows.length ? "p-0" : undefined}>
        {note && (
          <Alert variant={note.variant} className="m-3 mb-0 py-2 fs-sm" onClose={() => setNote(null)} dismissible>
            {note.text}
          </Alert>
        )}
        {rows.length === 0 ? (
          <div className="text-secondary fs-sm">
            ขณะนี้ไม่มีบัญชีที่ถูกกันไว้
            <div className="fs-xs">
              (ตัวนับเก็บอยู่ในหน่วยความจำของเซิร์ฟเวอร์ จึงเริ่มนับใหม่ทุกครั้งที่รีสตาร์ท)
            </div>
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-secondary">
                <tr>
                  <th>ผู้ใช้</th>
                  <th style={{ width: 130 }}>ประเภท</th>
                  <th style={{ width: 150 }}>พยายาม</th>
                  <th style={{ width: 170 }}>สถานะ</th>
                  <th style={{ width: 140 }}>ล่าสุด</th>
                  <th style={{ width: 120 }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.source}:${r.username}`}>
                    <td className="align-middle">
                      {r.name || <span className="text-secondary">ไม่พบเจ้าของรหัสผู้ใช้นี้</span>}
                      <div className="text-secondary fs-xs">{r.username}</div>
                    </td>
                    <td className="align-middle fs-sm">{SOURCE_LABEL[r.source] || r.source}</td>
                    <td className="align-middle fs-sm">
                      {r.attempts} / {r.max} ครั้ง
                      <div className="text-secondary fs-xs">ไม่สำเร็จ {r.fails} ครั้ง</div>
                    </td>
                    <td className="align-middle">
                      {r.locked ? (
                        <React.Fragment>
                          <Badge bg="danger">ถูกกันอยู่</Badge>
                          {resetText(r.resetTime) && (
                            <div className="text-secondary fs-xs">ปลดเองเวลา {resetText(r.resetTime)} น.</div>
                          )}
                        </React.Fragment>
                      ) : (
                        <Badge bg="warning">ใกล้ถึงเพดาน</Badge>
                      )}
                    </td>
                    <td className="align-middle fs-sm text-nowrap">{r.last_time_text || '—'}</td>
                    <td className="align-middle text-end">
                      <Button size="sm" variant="outline-danger" onClick={() => unlock(r)} disabled={busy}>
                        <i className="ri-lock-unlock-line me-1"></i>ปลดล็อก
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
